import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import ScaleFormularController from '../core/controllers/transform/ScaleFormularController';
import { InsideType, ModuleType } from '../../constants/enum';
import { GeometryUtils } from '../../utils/geometry/GeometryUtils';

export default class ModuleLoader {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
    this.scene = sceneManager.scene;
    this.initLoaders();
  }

  initLoaders() {
    this.gltfLoader = new GLTFLoader();
    this.dracoLoader = new DRACOLoader();

    this.dracoLoader.setDecoderPath(
      'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'
    );
    this.dracoLoader.setDecoderConfig({ type: 'js' });
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
  }

  async loadInsideModel(moduleData, position = new THREE.Vector3(0, 0, 0)) {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        moduleData.glbUrl,
        gltfScene => {
          const processedModule = this.processLoadedModule(
            gltfScene.scene,
            moduleData,
            position
          );

          resolve(processedModule);
        },
        undefined,
        error => reject(error)
      );
    });
  }

  async loadModule(moduleData, position = new THREE.Vector3(0, 0, 0)) {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        moduleData.glbUrl,
        gltfScene => {
          const processedModule = this.processLoadedModule(
            gltfScene.scene,
            moduleData,
            position
          );

          resolve(processedModule);
        },
        undefined,
        error => reject(error)
      );
    });
  }

  async loadAcessory(moduleData) {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        moduleData,
        gltfScene => {
          resolve(gltfScene.scene);
        },
        undefined,
        error => reject(error)
      );
    });
  }

  processLoadedModule(moduleGLTF, moduleData, position) {
    // Xử lý materials và shadows
    this.setupMaterialsAndShadows(moduleGLTF);

    // Tạo module group và chuyển đổi pivot
    const moduleGroup = this.changeModulePivot(
      // GeometryUtils.moduleWithoutAccessory(moduleGLTF),
      moduleGLTF,
      moduleData,
      position
    );

    const childrenToRemove = [];

    // Ẩn hoàn toàn mesh có tên chứa "MAT-DA"
    moduleGroup.traverse(child => {
      if (
        (child.isMesh &&
          child.name &&
          (child.name.toUpperCase().includes('MAT-DA') ||
            child.name.toUpperCase().includes('VACH-BEP'))) ||
        (child.name.toUpperCase().includes('NEP-NOC') &&
          (moduleData.moduleType.includes('upper') ||
            moduleData.moduleType.includes('ceiling'))) ||
        (child.name.toUpperCase().includes('BA-NGANG') &&
          moduleData.moduleType.includes('ceiling'))
      ) {
        // Ẩn hoàn toàn mesh
        child.visible = false;
        child.userData.type = InsideType.MESH_TEMPLATE;
        // Tắt raycast để không thể click
        child.raycast = function () {};
      }

      //Tạm thời set type cho cửa của tủ góc
      if (
        child.isMesh &&
        child.name &&
        moduleData.moduleType.toLowerCase().includes('corner') &&
        child.name.includes('DOOR')
      ) {
        child.visible = false;
        child.userData.type = InsideType.DOOR_TEMPLATE;

        childrenToRemove.push(child);
        // Tắt raycast để không thể click
        child.raycast = function () {};
      }

      //Tạm thời set type cho bìa cột tủ áo khuyết cột
      if (
        child.isMesh &&
        child.name &&
        moduleData.moduleType.toLowerCase().includes('column') &&
        (child.name.includes('BIA-COT') || child.name.includes('HAU-2'))
      ) {
        child.userData.type = InsideType.COL_PANEL;
      }

      //Tạm thời set type cho bìa cột tủ áo khuyết đà
      if (
        child.isMesh &&
        child.name &&
        moduleData.moduleType.toLowerCase().includes('beam') &&
        child.name.includes('BIA-COT')
      ) {
        child.userData.type = InsideType.COL_PANEL;
      }

      //Nệm đôn ngồi
      if (
        child.isMesh &&
        child.name &&
        moduleData.moduleType.toLowerCase().includes('bench') &&
        child.name.includes('NEM-NGOI')
      ) {
        child.userData.type = InsideType.STOOL_CUSHION;
        child.geometry.computeVertexNormals();
      }

      //Thanh treo quần áo cho tủ góc
      if (
        child.name &&
        moduleData.moduleType.toLowerCase().includes('corner') &&
        child.name.includes('THANH-TREO')
      ) {
        child.userData.type = InsideType.CLOTHES_HANGER;
        child.userData.noInside = true;
      }

      if (
        child.name &&
        moduleData.moduleType.toLowerCase().includes('bed') &&
        child.name.includes('DAY-HK')
      ) {
        child.userData.type = InsideType.DRAWER;
        child.userData.noInside = true;
      }

      if (
        child.name &&
        moduleData.moduleType.toLowerCase().includes('vanity') &&
        child.name.includes('DOOR')
      ) {
        child.userData.type = InsideType.DOOR;
        child.userData.noInside = true;
      }
      if (
        child.name &&
        moduleData.moduleType.toLowerCase().includes('vanity') &&
        child.name.includes('GUONG-BTD')
      ) {
        // child.userData.type = InsideType.DOOR;
        // child.userData.noInside = true;
        child.traverse(innerChild => {
          if (innerChild !== child) {
            innerChild.name = `GUONG-BTD_${innerChild.name}`;
          }
        });
      }

      //Set type cho xắn có sẵn cho tủ góc
      if (
        child.isMesh &&
        child.name &&
        moduleData.moduleType.toLowerCase().includes('corner') &&
        child.name.includes('XAN-NGANG')
      ) {
        child.userData.type = InsideType.HPANEL;
      }
    });

    childrenToRemove.forEach(child => {
      child.parent?.remove(child);
    });

    // //Hiển thị hậu tương ứng
    // if (moduleData.options.hauNgang !== undefined) {
    //   GeometryUtils.handleShowBackPanel(
    //     moduleGroup,
    //     moduleData.options.hauNgang
    //   );
    // }

    // Thêm vào scene
    this.scene.add(moduleGroup);

    return moduleGroup;
  }

  changeModulePivot(originalScene, moduleData) {
    // Tính size từ originalScene
    const box = new THREE.Box3().setFromObject(originalScene);
    const size = new THREE.Vector3();
    box.getSize(size);

    // Tạo group chính
    const moduleGroup = new THREE.Group();
    moduleGroup.userData = {
      type: 'module',
      requireCollision: true,
      moduleData: moduleData,
      originalSize: size,
      isSpecial: moduleData.moduleType.toLowerCase().includes('column')
        ? true
        : false,
    };

    moduleGroup.add(originalScene);

    return moduleGroup;
  }

  setupMaterialsAndShadows(scene) {
    scene.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;

        child.material.metalness = 0; // Không có tính kim loại
        child.material.roughness = 1; // Bề mặt thô (gỗ, sơn...)

        // // Độ trong suốt
        // child.material.transparent = true;
        // child.material.opacity = 0.2;

        child.material.needsUpdate = true;
      }
    });
  }

  cloneGLTFScene(originalScene) {
    const clonedScene = originalScene.clone(true);

    clonedScene.traverse(node => {
      if (node.isMesh) {
        const originalMaterial = node.material;
        node.material = originalMaterial.clone();
        node.castShadow = true;

        // Clone textures
        if (originalMaterial.map) {
          node.material.map = originalMaterial.map.clone();
        }
        if (originalMaterial.normalMap) {
          node.material.normalMap = originalMaterial.normalMap.clone();
        }
        if (originalMaterial.roughnessMap) {
          node.material.roughnessMap = originalMaterial.roughnessMap.clone();
        }
      }
    });

    return clonedScene;
  }

  dispose() {
    this.dracoLoader?.dispose();
    this.gltfLoader = null;
    this.dracoLoader = null;
    this.scene = null;
    this.sceneManager = null;
  }
}
